import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, API, getActiveAccountHeader } from '../App';
import { useSubscription } from '../contexts/SubscriptionContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { format, parseISO, isToday, isTomorrow, isThisWeek } from 'date-fns';
import { 
  LayoutDashboard, Users, Activity, Calendar, Bell, 
  TrendingUp, Clock, ChevronRight, Crown, Lock,
  Stethoscope, Scissors, Syringe
} from 'lucide-react';

export const StableDashboard = () => {
  const { token } = useAuth();
  const { hasStableDashboard, isPro, loading: subLoading } = useSubscription();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalHorses: 0,
    currentHorses: 0,
    formerHorses: 0,
    byTab: {},
    riders: 0,
    upcomingReminders: [],
    recentActivity: [],
    healthDue: []
  });

  // Get headers dynamically to ensure fresh activeAccountId after account switch
  const getHeaders = () => {
    const activeAccountId = localStorage.getItem('activeAccountId') || sessionStorage.getItem('activeAccountId');
    const h = { 'Content-Type': 'application/json' };
    if (activeAccountId) h['X-Active-Account'] = activeAccountId;
    if (token) h['Authorization'] = `Bearer ${token}`;
    return h;
  };

  useEffect(() => {
    if (!subLoading && !isPro()) {
      // Not a Pro user, redirect to account
      navigate('/account');
      toast.error('Stable Dashboard is a Pro feature');
      return;
    }
    if (!subLoading) {
      fetchDashboardData();
    }
  }, [token, subLoading]);

  const fetchDashboardData = async () => {
    const headers = getHeaders();
    try {
      const [horsesRes, ridersRes, remindersRes, healthRes] = await Promise.all([
        fetch(`${API}/horses`, { headers }),
        fetch(`${API}/rider-profiles`, { headers }),
        fetch(`${API}/reminders`, { headers }),
        fetch(`${API}/health-records`, { headers })
      ]);

      const horses = horsesRes.ok ? await horsesRes.json() : [];
      const riders = ridersRes.ok ? await ridersRes.json() : [];
      const reminders = remindersRes.ok ? await remindersRes.json() : [];
      const healthRecords = healthRes.ok ? await healthRes.json() : [];

      // Count horses by status and tab
      const currentHorses = horses.filter(h => h.status === 'current');
      const formerHorses = horses.filter(h => h.status === 'former');
      
      const byTab = {
        in_work: horses.filter(h => h.horse_tab === 'in_work').length,
        spelling: horses.filter(h => h.horse_tab === 'spelling').length,
        breeding: horses.filter(h => h.horse_tab === 'breeding').length,
        youngstock: horses.filter(h => h.horse_tab === 'youngstock').length,
        former: formerHorses.length
      };

      // Get upcoming reminders (next 7 days)
      const upcomingReminders = reminders
        .filter(r => !r.is_complete && r.due_date)
        .sort((a, b) => new Date(a.due_date) - new Date(b.due_date))
        .slice(0, 5);

      // Find health records that might be due soon
      const healthDue = [];
      const now = new Date();
      
      // Check for health records older than thresholds
      horses.forEach(horse => {
        const horseHealth = healthRecords.filter(h => h.horse_id === horse.horse_id);
        
        // Check last farrier (every 6-8 weeks)
        const lastFarrier = horseHealth.filter(h => h.record_type === 'farrier').sort((a, b) => new Date(b.date) - new Date(a.date))[0];
        if (lastFarrier) {
          const daysSince = Math.floor((now - new Date(lastFarrier.date)) / (1000 * 60 * 60 * 24));
          if (daysSince > 42) {
            healthDue.push({ horse, type: 'farrier', daysSince, icon: Scissors });
          }
        }

        // Check last worming (every 8-12 weeks)
        const lastWorming = horseHealth.filter(h => h.record_type === 'worming').sort((a, b) => new Date(b.date) - new Date(a.date))[0];
        if (lastWorming) {
          const daysSince = Math.floor((now - new Date(lastWorming.date)) / (1000 * 60 * 60 * 24));
          if (daysSince > 56) {
            healthDue.push({ horse, type: 'worming', daysSince, icon: Syringe });
          }
        }
      });

      setStats({
        totalHorses: horses.length,
        currentHorses: currentHorses.length,
        formerHorses: formerHorses.length,
        byTab,
        riders: riders.length,
        upcomingReminders,
        healthDue: healthDue.slice(0, 5)
      });
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (subLoading || loading) {
    return (
      <Layout>
        <div className="p-6 flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin w-8 h-8 border-4 border-forest border-t-transparent rounded-full" />
        </div>
      </Layout>
    );
  }

  if (!isPro()) {
    return (
      <Layout>
        <div className="p-6 text-center">
          <Lock className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-xl font-bold mb-2">Pro Feature</h2>
          <p className="text-muted-foreground mb-4">Stable Dashboard is available for Pro subscribers</p>
          <Link to="/account">
            <Button className="bg-forest hover:bg-forest-dark">Upgrade to Pro</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 md:p-6 lg:p-8 space-y-6" data-testid="stable-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl md:text-3xl font-bold font-serif text-foreground">Stable Dashboard</h1>
              <Badge className="bg-forest text-white gap-1">
                <Crown className="w-3 h-3" /> Pro
              </Badge>
            </div>
            <p className="text-muted-foreground mt-1">Overview of your entire stable</p>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card className="border-0 shadow-card bg-white">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-forest">{stats.totalHorses}</p>
              <p className="text-sm text-muted-foreground">Total Horses</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-card bg-white">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-saddle">{stats.currentHorses}</p>
              <p className="text-sm text-muted-foreground">Current</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-card bg-white">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-amber-600">{stats.byTab.in_work}</p>
              <p className="text-sm text-muted-foreground">In Work</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-card bg-white">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-blue-600">{stats.byTab.spelling}</p>
              <p className="text-sm text-muted-foreground">Spelling</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-card bg-white">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-pink-600">{stats.byTab.breeding}</p>
              <p className="text-sm text-muted-foreground">Breeding</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-card bg-white">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-purple-600">{stats.riders}</p>
              <p className="text-sm text-muted-foreground">Riders</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Upcoming Reminders */}
          <Card className="border-0 shadow-card bg-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Bell className="w-5 h-5 text-amber-500" />
                Upcoming Reminders
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats.upcomingReminders.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">No upcoming reminders</p>
              ) : (
                <div className="space-y-3">
                  {stats.upcomingReminders.map((reminder, i) => {
                    const dueDate = parseISO(reminder.due_date);
                    const isUrgent = isToday(dueDate) || isTomorrow(dueDate);
                    
                    return (
                      <div 
                        key={reminder.reminder_id || i} 
                        className={`flex items-center justify-between p-3 rounded-lg ${isUrgent ? 'bg-amber-50 border border-amber-200' : 'bg-muted/30'}`}
                      >
                        <div>
                          <p className="font-medium">{reminder.title}</p>
                          <p className="text-sm text-muted-foreground">{reminder.horse_name}</p>
                        </div>
                        <Badge variant={isUrgent ? 'destructive' : 'outline'}>
                          {isToday(dueDate) ? 'Today' : isTomorrow(dueDate) ? 'Tomorrow' : format(dueDate, 'MMM d')}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              )}
              <Link to="/reminders">
                <Button variant="ghost" className="w-full mt-4 text-forest">
                  View All Reminders <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardContent>
          </Card>

          {/* Health Due */}
          <Card className="border-0 shadow-card bg-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Stethoscope className="w-5 h-5 text-terracotta" />
                Health Attention Needed
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats.healthDue.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">All horses up to date!</p>
              ) : (
                <div className="space-y-3">
                  {stats.healthDue.map((item, i) => {
                    const Icon = item.icon;
                    return (
                      <Link 
                        key={i} 
                        to={`/horses/${item.horse.horse_id}?tab=health`}
                        className="flex items-center justify-between p-3 rounded-lg bg-terracotta/5 border border-terracotta/20 hover:bg-terracotta/10 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-terracotta/10 rounded-full">
                            <Icon className="w-4 h-4 text-terracotta" />
                          </div>
                          <div>
                            <p className="font-medium">{item.horse.stable_name}</p>
                            <p className="text-sm text-muted-foreground capitalize">{item.type} due</p>
                          </div>
                        </div>
                        <Badge variant="outline" className="text-terracotta border-terracotta">
                          {item.daysSince} days ago
                        </Badge>
                      </Link>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Links */}
        <Card className="border-0 shadow-card bg-white">
          <CardHeader>
            <CardTitle className="text-lg">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Link to="/horses">
                <Button variant="outline" className="w-full h-auto py-4 flex flex-col gap-2">
                  <LayoutDashboard className="w-5 h-5" />
                  <span>View Horses</span>
                </Button>
              </Link>
              <Link to="/rider-profiles">
                <Button variant="outline" className="w-full h-auto py-4 flex flex-col gap-2">
                  <Users className="w-5 h-5" />
                  <span>Manage Riders</span>
                </Button>
              </Link>
              <Link to="/reports">
                <Button variant="outline" className="w-full h-auto py-4 flex flex-col gap-2">
                  <TrendingUp className="w-5 h-5" />
                  <span>Generate Reports</span>
                </Button>
              </Link>
              <Link to="/expenses">
                <Button variant="outline" className="w-full h-auto py-4 flex flex-col gap-2">
                  <Activity className="w-5 h-5" />
                  <span>Finance Vault</span>
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};
